// All course content for AI-Powered Business Analytics Professional Program

export const HERO = {
  eyebrow: 'ExcellenC · Professional Program',
  title1: 'AI-Powered',
  title2: 'Business Analytics',
  title3: 'Professional Program',
  description:
    "Live, mentor-led training built for the AI era — blending real-world business case studies, Generative AI, Agentic AI, and the industry-standard analytics tools that transform data into strategic business decisions.",
  stats: [
    { value: '5 Months', label: 'Intensive Program' },
    { value: 'Live', label: 'Instructor-Led' },
    { value: 'Max 15', label: 'Learners / Batch' },
  ],
};

export const OVERVIEW = {
  eyebrow: 'The Program',
  title: 'Built for the ',
  titleAccent: 'AI era of business.',
  intro:
    "For graduates, final-year students, career switchers and working professionals ready to build or advance a career in Business Analytics, BI and AI-driven decision-making.",
  cards: [
    {
      n: '01',
      title: 'Learn Live, Not Recorded',
      body: 'Every session is delivered live by industry mentors. Ask questions, get unstuck in real time, and learn the way high-performing analytics teams actually work.',
    },
    {
      n: '02',
      title: 'Build a Real Portfolio',
      body: "Module-based challenges plus two end-to-end industry projects that prove you can solve real business problems — not just pass quizzes.",
    },
    {
      n: '03',
      title: 'AI Woven Into Everything',
      body: "From prompt engineering to building your own Analyst AI Agents, you'll wield Generative AI and Agentic AI across Excel, SQL, Python and BI.",
    },
    {
      n: '04',
      title: 'Career, Handled',
      body: 'Unlimited AI-powered mock interviews, structured placement assistance and a dedicated success team that stays with you until you land the role.',
    },
  ],
};

export const LEARN_PILLARS = {
  eyebrow: "What You'll Learn",
  title: 'From raw data to ',
  titleAccent: 'real business decisions.',
  intro:
    'A complete business analyst skillset — the technical craft, the business acumen and the AI leverage that sets you apart.',
  pillars: [
    { title: 'Collect & Clean', body: 'Master data collection, quality and governance so your business insights start from solid ground.' },
    { title: 'Analyze', body: 'Apply statistics, KPI thinking and structured problem-solving to find the story hidden in the numbers.' },
    { title: 'Visualize', body: 'Design dashboards in Tableau & Power BI that make executives act on your insights.' },
    { title: 'Leverage AI', body: 'Use Generative AI and custom Analyst Agents to automate reporting and 10x your business output.' },
  ],
};

export const KEY_FEATURES = {
  eyebrow: 'Why ExcellenC',
  title: 'An experience engineered around ',
  titleAccent: 'your outcome.',
  items: [
    { icon: 'Radio', title: 'Live Instructor-Led Learning', body: 'Real mentors, real-time feedback, zero passive video watching.' },
    { icon: 'Users', title: 'Maximum 15 Learners Per Batch', body: 'Boutique batch sizes so you are never just a name on a screen.' },
    { icon: 'Briefcase', title: 'Real-Time Industry Projects', body: 'Work on business problems sourced from actual company scenarios.' },
    { icon: 'Sparkles', title: 'AI-Integrated Curriculum', body: 'Generative AI and Analyst AI Agents built into every module.' },
    { icon: 'Compass', title: 'Structured Career Assistance', body: 'Mock interviews, resume craft and placement support.' },
    { icon: 'Infinity', title: 'Lifetime Resource Access', body: 'Recordings, materials, assignments and revision content — forever.' },
  ],
};

export const TOOLS = {
  eyebrow: "Tools You'll Master",
  title: 'The industry-standard stack, ',
  titleAccent: 'end to end.',
  tools: [
    { n: '01', name: 'Excel', sub: 'Advanced + VBA' },
    { n: '02', name: 'SQL', sub: 'Queries to Triggers' },
    { n: '03', name: 'Tableau', sub: 'Interactive Dashboards' },
    { n: '04', name: 'Power BI', sub: 'DAX + AI Visuals' },
    { n: '05', name: 'Python', sub: 'Pandas · NumPy' },
    { n: '06', name: 'Generative AI', sub: 'Prompting + Agents' },
    { n: '07', name: 'Analyst AI Agents', sub: 'Workflow Automation' },
    { n: '08', name: 'Business Stats', sub: 'Probability · Regression' },
    { n: '09', name: 'JIRA', sub: 'Agile · Sprints' },
  ],
};

export const CURRICULUM = {
  eyebrow: 'The Curriculum',
  title: 'Ten phases. ',
  titleAccent: 'One career-ready business analyst.',
  intro:
    'A structured path from data foundations to autonomous Analyst AI Agents and agile business delivery — every phase stacks on the last.',
  phases: [
    {
      title: 'Data Foundation',
      topics: [
        'Introduction to Data and Analytics',
        'Types of Data',
        'Data Lifecycle',
        'Database Fundamentals',
        'Data Quality Concepts',
        'Data Governance Basics',
      ],
    },
    {
      title: 'Advanced Excel',
      topics: [
        'Getting Started with Excel',
        'Cell References, Named Ranges & Arithmetic Functions',
        'Logical Functions and Conditional Formulas',
        'Cross-Sheet References, Lookup Functions & Nested IF',
        'Advanced Excel Functions',
        'Date, Time & Text Functions',
        'Data Management: Cleaning, Duplicates, Formatting & Filtering',
        'Data Visualization: Conditional Formatting & Charts',
        'Pivot Tables & Pivot Charts',
        'Dashboard Development & Best Practices',
        'Power Query, PivotTables & Power Pivot',
        'VBA Programming & Macro Automation',
      ],
    },
    {
      title: 'SQL',
      topics: [
        'Introduction to SQL',
        'SQL Fundamentals',
        'Syntax, Commands & Data Types',
        'Data Query Language (DQL) & Operators',
        'CASE Statements & NULL Handling',
        'Aggregate Functions & Grouping',
        'Database Constraints',
        'SQL Join Operations',
        'Data Definition Language (DDL)',
        'DML & Transaction Control (TCL)',
        'Indexes & Database Views',
        'Stored Procedures',
        'User-Defined Functions & Control Constructs',
        'UNION, INTERSECT & Subqueries',
        'Exception & Error Handling',
        'Database Triggers',
      ],
    },
    {
      title: 'Tableau',
      topics: [
        'Tableau Fundamentals',
        'Chart Creation & Formatting Basics',
        'Data Filtering Techniques',
        'Calculated Fields & Expressions',
        'Data Blending & Joining Techniques',
        'Advanced & Custom Visualizations',
        'Groups, Bins, Hierarchies, Sets & Parameters',
        'Analytics Features & Dashboard Design',
        'Dashboard Interactivity & Tableau Public',
      ],
    },
    {
      title: 'Power BI',
      topics: [
        'Introduction to Power BI & Installation',
        'Interface, Data Sources & Visualizations',
        'Building Sample Dashboards with Animated Visuals',
        'AI-Powered Visualizations in Power BI',
        'Interactive Visualizations',
        'Power Query Editor Essentials',
        'Data Modeling in Power BI',
        'Data Filtering with Power Query',
        'Data Transformation & Customization',
        'DAX Formulas and Expressions',
        'Custom Visual Development',
        'Power BI Service & Cloud Publishing',
      ],
    },
    {
      title: 'Python',
      topics: [
        'Introduction to Python and Installation',
        'Data Structures',
        'Control Statements',
        'Functions',
        'Regex, File Handling',
        'Logging, DateTime, JSON, MySQL Integration',
        'Object-Oriented Programming (OOP)',
        'Web Scraping',
        'Pandas',
        'NumPy and Matplotlib',
      ],
    },
    {
      title: 'Generative AI',
      topics: [
        'Introduction to Generative AI',
        'Prompt Engineering Techniques',
        'Generative AI in Excel',
        'Python Development with Generative AI',
        'Generative AI for SQL Coding',
        'Generative AI for Data Analysis & Visualization',
      ],
    },
    {
      title: 'Analyst AI Agent Development',
      topics: [
        'Introduction to Analyst AI Agents',
        'KPI Monitoring Agents',
        'Reporting Agents',
        'Data Quality Agents',
        'Forecasting Agents',
        'Multi-Agent Analytics Ecosystem',
      ],
    },
    {
      title: 'Business Statistics',
      topics: [
        'Descriptive Statistics',
        'Probability and Normal Distribution',
        'Inferential Statistics',
        'Data Cleaning and Insights',
      ],
    },
    {
      title: 'JIRA',
      topics: [
        'Introduction to JIRA',
        'Creating JIRA Board and Issues',
        'Filter and Search Options',
        'Generating Reports and Creating Road Maps',
        'Board Settings and Case Study',
      ],
    },
  ],
};

export const PROJECTS = {
  eyebrow: 'Projects & Portfolio',
  title: 'You graduate with ',
  titleAccent: 'proof, not just a certificate.',
  intro: 'Build in-hand experience from every module and every tool — then prove it with two complete, real-world business projects.',
  cards: [
    {
      n: '01',
      title: 'A Project With Every Module',
      body: "You don't just learn a tool — you ship with it. Each module ends in a hands-on project, so you gain practical, in-hand experience from every module and every tool as you go.",
    },
    {
      n: '02',
      title: '2 End-to-End Industry Projects',
      body: 'Two complete, real-world business projects that take you from raw data all the way to decision-ready insight — the exact kind of work recruiters want to see.',
    },
    {
      n: '03',
      title: '100% Real-Time Projects',
      body: 'No sample datasets. All projects are real-time industry projects from our technology partner Intellectra AI.',
    },
  ],
};

export const INTERNSHIP = {
  eyebrow: 'Real-Time Industry Projects & Internship',
  title: 'Real experience, ',
  titleAccent: 'before your first job.',
  intro:
    'Learn the way real business analytics teams work — on live problem statements, with real data, tools and deadlines. Your project work doubles as genuine industry experience.',
  bullets: [
    'Real-time industry projects sourced from Intellectra AI',
    'Internship-style experience on live business problem statements',
    'Work end-to-end: collect, clean, analyze, visualize, present',
    'Portfolio-ready deliverables you can showcase to employers',
  ],
  stats: [
    { value: '7', label: 'Module Projects' },
    { value: '2', label: 'End-to-End Projects' },
    { value: '100%', label: 'Real-Time Data' },
  ],
};

export const PLACEMENT = {
  eyebrow: 'Career Success & Placement Ecosystem',
  title: "We don't stop at teaching. ",
  titleAccent: 'We get you hired.',
  intro:
    'Skills get you in the room — our Career Success ecosystem gets you the offer. Structured placement training includes in-person mock interviews as part of the course with detailed personalised feedback, plus additional access to our AI mock interview platform for unlimited practice.',
  cards: [
    { icon: 'FileText', title: 'Resume Building Session', body: 'Craft a recruiter-ready resume that puts your real project work front and centre.' },
    { icon: 'Linkedin', title: 'LinkedIn Profile Optimisation', body: 'Build a standout profile that gets you noticed by recruiters and hiring managers.' },
    { icon: 'Users', title: 'In-Person Mock Interviews', body: 'Face-to-face mock interviews as part of the course, with detailed personalised feedback.' },
    { icon: 'BotMessageSquare', title: 'AI Mock Interview Platform', body: 'Additional unlimited practice on our PlacementPilot AI platform, anytime you want.' },
    { icon: 'Briefcase', title: 'Job Portal Access', body: 'Curated internships and full-time roles you can apply to directly, in a few clicks.' },
    { icon: 'LifeBuoy', title: 'Dedicated Success Team', body: 'Real mentors who stay with you from your first module to your first offer.' },
  ],
  timeline: [
    { n: 1, title: 'Placement Training', sub: 'Resume + LinkedIn' },
    { n: 2, title: 'In-Person Mock Interviews', sub: 'Detailed feedback' },
    { n: 3, title: 'AI Practice', sub: 'Unlimited on PlacementPilot' },
    { n: 4, title: 'Job Portal', sub: 'Apply to curated roles' },
    { n: 5, title: 'Offer', sub: 'Land the role' },
  ],
};

export const PLACEMENT_PILOT = {
  eyebrow: 'AI Mock Interviews',
  title: 'PlacementPilot',
  by: 'by ExcellenC',
  headline: 'Accelerate your journey from learning to landing your dream career.',
  description:
    "PlacementPilot is ExcellenC's AI-powered Career Success Platform — practise unlimited voice-enabled mock interviews, get instant AI feedback on every answer, and walk into every real interview with confidence.",
  stats: [
    { value: '5', label: 'Interview Rounds' },
    { value: '15', label: 'Questions / Session' },
    { value: '3', label: 'Career Tracks' },
    { value: 'AI', label: 'Powered Feedback' },
  ],
  rounds: ['Technical', 'Case Study', 'Behavioral / HR', 'Final Review', 'Salary Negotiation'],
  features: [
    { title: 'AI-Powered Evaluation', body: 'Every answer is scored out of 10 with specific strengths and improvements.' },
    { title: 'Voice-Enabled', body: 'Speak your answers out loud — a true-to-life interview experience.' },
    { title: 'Course-Aligned Questions', body: 'Questions adapt to your track: Business Analytics, Data Analytics, Data Science & AI.' },
    { title: 'Full 5-Round Journey', body: 'From technical rounds all the way to salary negotiation.' },
    { title: 'Model Answers', body: 'Compare your response to an ideal expected answer, every time.' },
    { title: 'Integrated Job Portal', body: 'Explore internships and full-time roles without leaving the platform.' },
  ],
  previews: [
    { title: 'Landing Experience', url: 'https://customer-assets-rejwkqb3.emergentagent.net/job_analytics-journey-8/artifacts/u0jmgzt9_Placement%20Pilot%20-%20Intro%20Page.JPG' },
    { title: 'Practice Smart, Get Placed Faster', url: 'https://customer-assets-rejwkqb3.emergentagent.net/job_analytics-journey-8/artifacts/y2jarrdq_Placement%20Pilot%20-%20Sign%20In%20page.JPG' },
    { title: 'Choose Your Career Track', url: 'https://customer-assets-rejwkqb3.emergentagent.net/job_analytics-journey-8/artifacts/6efv5vm3_Placement%20Pilot%20Home%20Page.JPG' },
    { title: 'Voice-Enabled Interview', url: 'https://customer-assets-rejwkqb3.emergentagent.net/job_analytics-journey-8/artifacts/mtqgo7ft_Placement%20Pilot%20Interview%20Page%201.JPG' },
    { title: 'AI Evaluation & Feedback', url: 'https://customer-assets-rejwkqb3.emergentagent.net/job_analytics-journey-8/artifacts/n8twal2z_Placement%20Pilot%20Interview%20Page%202.JPG' },
    { title: 'Integrated Job Portal', url: 'https://customer-assets-rejwkqb3.emergentagent.net/job_analytics-journey-8/artifacts/t1qz12xh_Placement%20Pilot%20-%20Job%20Portal.JPG' },
  ],
};

export const CAREERS = {
  eyebrow: 'Career Opportunities',
  title: '12+ roles. ',
  titleAccent: 'One skillset. Yours.',
  intro: 'Structured placement assistance opens doors across the entire business analytics landscape.',
  roles: [
    'Business Analyst', 'BI Analyst', 'Reporting Analyst', 'MIS Analyst',
    'Operations Analyst', 'Process Analyst', 'Project Analyst', 'Product Analyst',
    'AI Business Analyst', 'Analytics Consultant', 'Decision Support Analyst', 'Strategy Consultant',
    'Data Analyst', 'Business Intelligence Developer', 'Insights Analyst',
  ],
};

export const INCLUDED = {
  eyebrow: 'Everything Included',
  title: 'Everything in your ',
  titleAccent: 'learning journey.',
  intro: 'One program, everything you need — from your first live class to your first business analytics offer.',
  items: [
    'Live instructor-led training (5 months)',
    'Maximum 15 learners per batch',
    '10-phase AI-integrated curriculum',
    'A hands-on project with every module',
    '2 end-to-end real-time industry projects',
    'Internship experience with Intellectra AI',
    'Unlimited AI-powered mock interviews',
    'Resume & portfolio building',
    'Structured placement assistance',
    'Lifetime access to recordings & resources',
    'Mentorship from experienced industry experts',
    'Completion, project & internship certificates',
  ],
};

export const CERTS = {
  eyebrow: 'Certifications',
  title: 'Certificates that ',
  titleAccent: 'open doors.',
  intro:
    "You don't finish with one certificate — you finish with a stack. Dual course-completion certification (co-branded with Texas Tech University), real end-to-end business project certificates, and a certificate for every module-based project — all from our technology partner Intellectra AI.",
  completion: [
    { title: 'AI-Powered Business Analytics Professional Program', issuer: 'Issued by ExcellenC', url: 'https://customer-assets-4nw71qhi.emergentagent.net/job_ai-analytics-site/artifacts/npw9vkg3_ExcellenC%20-%20AI-Powered%20Business%20Analytics.png' },
    { title: 'AI-Powered Business Analytics Professional Program', issuer: 'Issued by Texas Tech University', url: 'https://customer-assets-4nw71qhi.emergentagent.net/job_ai-analytics-site/artifacts/zo9s89ro_Texas%20Tech%20University%20-%20AI%20Powered%20Business%20Analytics.png' },
  ],
  endToEnd: [
    { title: 'Global Logistics & Port Operations Analytics', issuer: 'Issued by Intellectra AI', url: 'https://customer-assets-rejwkqb3.emergentagent.net/job_analytics-journey-8/artifacts/w8ea5r5b_End%20to%20End%20Project%201.png' },
    { title: 'Retail Chain Profit Leakage & Demand Optimization', issuer: 'Issued by Intellectra AI', url: 'https://customer-assets-rejwkqb3.emergentagent.net/job_analytics-journey-8/artifacts/4jei4i1f_End%20to%20End%20Project%202.png' },
  ],
  modules: [
    { tool: 'Excel', title: 'Financial, Marketing & Operational Analysis', url: 'https://customer-assets-rejwkqb3.emergentagent.net/job_analytics-journey-8/artifacts/zs9fy1e9_Excel%20Project.png' },
    { tool: 'SQL', title: 'EdTech Subscription Analytics Platform', url: 'https://customer-assets-rejwkqb3.emergentagent.net/job_analytics-journey-8/artifacts/cy1bb7m5_SQL%20Project.png' },
    { tool: 'Tableau', title: 'Hospital Patient Analytics & Outcome Dashboard', url: 'https://customer-assets-rejwkqb3.emergentagent.net/job_analytics-journey-8/artifacts/s2eep9f3_Tableau%20Project.png' },
    { tool: 'Power BI', title: 'RLS-Enabled Regional Sales & Profit Analytics', url: 'https://customer-assets-rejwkqb3.emergentagent.net/job_analytics-journey-8/artifacts/htatyahx_Power%20BI%20Project.png' },
    { tool: 'Python', title: 'Product Insights via Web Scraping & Analysis', url: 'https://customer-assets-rejwkqb3.emergentagent.net/job_analytics-journey-8/artifacts/4wip04xq_Python%20Project.png' },
    { tool: 'Generative & Agentic AI', title: 'Business Analyst AI Copilot', url: 'https://customer-assets-rejwkqb3.emergentagent.net/job_analytics-journey-8/artifacts/sesk2gx8_Gen%20AI%20and%20Agentic%20AI%20Project.png' },
    { tool: 'JIRA', title: 'Planning and Tracking of an E-Commerce Website Launch', url: 'https://customer-assets-4nw71qhi.emergentagent.net/job_ai-analytics-site/artifacts/csdzxstg_JIRA%20Project.png' },
  ],
  internship: [
    { title: 'Internship Offer Letter', sub: 'Issued during enrollment' },
    { title: 'Internship Completion Letter', sub: 'Issued at course completion' },
    { title: 'Internship Experience Letter', sub: 'Covering the entire course duration' },
  ],
};

export const FEE = {
  eyebrow: 'Course Fee',
  title: 'Your career upgrade, ',
  titleAccent: 'made accessible.',
  price: '₹45,000',
  taxNote: 'including GST',
  honestyLabel: 'Honest Pricing',
  honestyNote:
    'No inflated MRP. No dressed-up discount. Just one straightforward fee for the program that gets you career-ready.',
  perks: [
    'All 10 phases + 2 end-to-end industry projects',
    'Lifetime access to recordings & resources',
    'Unlimited AI-powered mock interviews',
    'Structured placement assistance',
  ],
  rmNote: 'Contact your relationship manager to complete the enrollment procedures.',
};
